import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

type Tpl = {
  id: string;
  template_key: string;
  subject: string;
  body_html: string;
  enabled: boolean;
  bcc_admin: boolean;
};

const VARS: Record<string, string[]> = {
  invitation: ['first_name', 'last_name', 'email', 'company_name', 'role_name', 'invitation_url'],
  direct_welcome: ['first_name', 'last_name', 'email', 'password', 'company_name', 'role_name', 'login_url'],
  password_reset: ['first_name', 'last_name', 'email', 'reset_url', 'login_url'],
  signup_welcome: ['first_name', 'last_name', 'email', 'company_name', 'login_url'],
  email_change: ['first_name', 'last_name', 'email', 'new_email', 'confirm_url', 'login_url'],
};


const TemplateEditor = ({ tpl, onSave, saving }: { tpl: Tpl; onSave: (t: Tpl) => void; saving: boolean }) => {
  const { t } = useTranslation();
  const [local, setLocal] = useState<Tpl>(tpl);
  useEffect(() => setLocal(tpl), [tpl]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="text-[#5A6B7A]">{t('emailTemplates.variables')}:</span>
        {VARS[tpl.template_key]?.map((v) => (
          <code key={v} className="bg-[#EBF4F8] text-[#0880AE] px-2 py-1 rounded">{`{{${v}}}`}</code>
        ))}
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg border border-[#DBE2EA]">
        <div>
          <Label>{t('emailTemplates.enabled')}</Label>
          <p className="text-xs text-[#5A6B7A]">{t('emailTemplates.enabledHint')}</p>
        </div>
        <Switch checked={local.enabled} onCheckedChange={(c) => setLocal({ ...local, enabled: c })} />
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg border border-[#DBE2EA]">
        <div>
          <Label>{t('emailTemplates.bccAdmin')}</Label>
          <p className="text-xs text-[#5A6B7A]">{t('emailTemplates.bccAdminHint')}</p>
        </div>
        <Switch checked={local.bcc_admin} onCheckedChange={(c) => setLocal({ ...local, bcc_admin: c })} />
      </div>
      <div className="space-y-2">
        <Label>{t('emailTemplates.subject')}</Label>
        <Input value={local.subject} onChange={(e) => setLocal({ ...local, subject: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label>{t('emailTemplates.bodyHtml')}</Label>
        <Textarea
          rows={16}
          className="font-mono text-xs"
          value={local.body_html}
          onChange={(e) => setLocal({ ...local, body_html: e.target.value })}
        />
      </div>
      <Button
        onClick={() => onSave(local)}
        disabled={saving}
        className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
      >
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('common.save')}
      </Button>
    </div>
  );
};

export const EmailTemplatesTab = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .in('template_key', [
          'invitation',
          'direct_welcome',
          'password_reset',
          'signup_welcome',
          'email_change',
        ]);
      if (error) throw error;
      return data as Tpl[];
    },
  });

  const mutation = useMutation({
    mutationFn: async (t: Tpl) => {
      const { error } = await supabase
        .from('email_templates')
        .update({
          subject: t.subject,
          body_html: t.body_html,
          enabled: t.enabled,
          bcc_admin: t.bcc_admin,
        })
        .eq('id', t.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('common.success'));
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: (e: any) => toast.error(e.message || t('common.error')),
  });

  const invitation = data?.find((x) => x.template_key === 'invitation');
  const welcome = data?.find((x) => x.template_key === 'direct_welcome');
  const passwordReset = data?.find((x) => x.template_key === 'password_reset');
  const signupWelcome = data?.find((x) => x.template_key === 'signup_welcome');
  const emailChange = data?.find((x) => x.template_key === 'email_change');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('emailTemplates.title')}</CardTitle>
        <CardDescription>{t('emailTemplates.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading || !invitation || !welcome ? (
          <Loader2 className="animate-spin" />
        ) : (
          <Tabs defaultValue="invitation">
            <TabsList>
              <TabsTrigger value="invitation">{t('emailTemplates.invitation')}</TabsTrigger>
              <TabsTrigger value="direct_welcome">{t('emailTemplates.directWelcome')}</TabsTrigger>
              {passwordReset && (
                <TabsTrigger value="password_reset">{t('emailTemplates.passwordReset')}</TabsTrigger>
              )}
              {signupWelcome && (
                <TabsTrigger value="signup_welcome">{t('emailTemplates.signupWelcome')}</TabsTrigger>
              )}
              {emailChange && (
                <TabsTrigger value="email_change">{t('emailTemplates.emailChange')}</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="invitation" className="mt-4">
              <TemplateEditor tpl={invitation} onSave={(t) => mutation.mutate(t)} saving={mutation.isPending} />
            </TabsContent>
            <TabsContent value="direct_welcome" className="mt-4">
              <TemplateEditor tpl={welcome} onSave={(t) => mutation.mutate(t)} saving={mutation.isPending} />
            </TabsContent>
            {passwordReset && (
              <TabsContent value="password_reset" className="mt-4">
                <TemplateEditor tpl={passwordReset} onSave={(t) => mutation.mutate(t)} saving={mutation.isPending} />
              </TabsContent>
            )}
            {signupWelcome && (
              <TabsContent value="signup_welcome" className="mt-4">
                <TemplateEditor tpl={signupWelcome} onSave={(t) => mutation.mutate(t)} saving={mutation.isPending} />
              </TabsContent>
            )}
            {emailChange && (
              <TabsContent value="email_change" className="mt-4">
                <TemplateEditor tpl={emailChange} onSave={(t) => mutation.mutate(t)} saving={mutation.isPending} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </CardContent>

    </Card>
  );
};
